import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useChat } from '../../context/ChatContext';
import chatService from '../../services/chatService';
import { useVideoCall } from '../../context/VideoCallContext';
import videoCallService from '../../services/videoCallService';
import { notify } from '../CustomToast';
import { getEcho } from '../../echo';
import ChatHeader from './ChatWindow/ChatHeader';
import SearchMessages from './ChatWindow/SearchMessages';
import MessageBubble from './ChatWindow/MessageBubble';
import ForwardMessageModal from './ChatWindow/ForwardMessageModal';
import DeleteMessageModal from './ChatWindow/DeleteMessageModal';
import EditMessageModal from './ChatWindow/EditMessageModal';
import DateDivider from './ChatWindow/DateDivider';
import TypingIndicator from './ChatWindow/TypingIndicator';
import MessageInput from './ChatWindow/MessageInput';
import { MessageListSkeleton } from './TalkSkeleton';
import ParticipantsModal from './ChatWindow/ParticipantsModal';
import TalkCallHistoryModal from './ChatWindow/TalkCallHistoryModal';
import TalkAddMembersModal from './ChatWindow/TalkAddMembersModal';
import TalkAttachmentOnlyOfficeViewerModal from './TalkAttachmentOnlyOfficeViewerModal';
import TalkAttachmentPdfViewerModal from './TalkAttachmentPdfViewerModal';
import TalkAttachmentImageViewerModal from './TalkAttachmentImageViewerModal';
import TalkAttachmentUnsupportedModal from './TalkAttachmentUnsupportedModal';

const ChatWindow = () => {
    const { activeConversationId, conversations, setActiveConversationId, setHideSidebar, fetchConversations,
        hasMore: hasMoreConversations, isLoadingMore: loadingMoreConversations, loadMoreConversations,
        deletionRevision, applyDeletion, reconcileMessages, mergeMessages, rememberHiddenMessages } = useChat();
    const [forwardingMessage, setForwardingMessage] = useState(null);
    const [deletingMessage, setDeletingMessage] = useState(null);
    const [editingMessage, setEditingMessage] = useState(null);
    const conversationRef = useRef(activeConversationId);
    conversationRef.current = activeConversationId;
    const historyRequest = useRef(0);
    const [messages, setMessages] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(1);
    const [typingUsers, setTypingUsers] = useState([]);
    const [showSearch, setShowSearch] = useState(false);
    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const messageRefs = useRef({});
    const [currentUser, setCurrentUser] = useState(null);
    const { joinMeeting } = useVideoCall();
    const [meetingLoading, setMeetingLoading] = useState(false);
    const [showParticipantsModal, setShowParticipantsModal] = useState(false);

    // Call history modal state
    const [showCallHistory, setShowCallHistory] = useState(false);
    const [callHistory, setCallHistory] = useState([]);
    const [callHistoryLoading, setCallHistoryLoading] = useState(false);

    // Add members modal state
    const [showAddMembersModal, setShowAddMembersModal] = useState(false);

    // Attachment viewer state
    const [viewerState, setViewerState] = useState(null); // { kind, message, upload } | null

    const handleAttachmentClick = (message, upload, kind) => {
        setViewerState({ kind, message, upload });
    };

    const closeViewer = () => setViewerState(null);

    const conversation = conversations.find(c => c.id === activeConversationId);

    const scrollToBottom = useCallback((behavior = 'smooth') => {
        messagesEndRef.current?.scrollIntoView({ behavior });
    }, []);

    const loadMessages = useCallback(async (pageNum = 1) => {
        const conversationId = activeConversationId;
        const requestId = ++historyRequest.current;
        if (pageNum === 1) setIsLoading(true);
        else setIsFetchingMore(true);

        try {
            const res = await chatService.getMessages(activeConversationId, pageNum);
            if (conversationRef.current !== conversationId || requestId !== historyRequest.current) return;
            rememberHiddenMessages(res.data?.hidden_message_ids || [], conversationId);
            // Backend returns { data: { messages: [...], pagination: {...} } }
            // Or directly array if no pagination

            let newMessages = [];
            let pagination = null;

            if (Array.isArray(res)) {
                newMessages = res;
            } else if (res.data) {
                newMessages = Array.isArray(res.data) ? res.data : (res.data.messages || []);
                pagination = res.data.pagination;
            }

            // Recovered deletions must invalidate open previews/viewers as well
            // as bubbles, just like a live event received while online.
            newMessages.filter(message => message.deleted_at).forEach(message => applyDeletion({
                message_id: message.id, conversation_id: conversationId,
                scope: 'everyone', deleted_at: message.deleted_at,
            }));

            // Backend returns newest first (descending). Reverse them to display oldest first.
            const reversedMessages = [...newMessages].reverse();

            if (pageNum === 1) {
                // Preserve events received while the history request was in flight.
                setMessages(current => mergeMessages(current, reversedMessages));
                setHasMore(pagination ? pagination.current_page < pagination.last_page : false);
                // Scroll to bottom after render
                setTimeout(() => scrollToBottom(), 100);
            } else {
                setMessages(prev => mergeMessages(prev, reversedMessages));
                setHasMore(pagination ? pagination.current_page < pagination.last_page : false);

                // Restore scroll position
                if (messagesContainerRef.current) {
                    const container = messagesContainerRef.current;
                    const oldScrollHeight = container.scrollHeight;
                    const oldScrollTop = container.scrollTop; // usually near 0

                    // After render, adjust scroll
                    requestAnimationFrame(() => {
                        const newScrollHeight = container.scrollHeight;
                        container.scrollTop = newScrollHeight - oldScrollHeight + oldScrollTop;
                    });
                }
            }

            setPage(pageNum);

        } catch (error) {
            console.error('Failed to load messages', error);
        } finally {
            if (conversationRef.current === conversationId && requestId === historyRequest.current) {
                if (pageNum === 1) setIsLoading(false);
                setIsFetchingMore(false);
            }
        }
    }, [activeConversationId, mergeMessages, rememberHiddenMessages, scrollToBottom, applyDeletion]);

    // Get current user
    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('user'));
        setCurrentUser(user);
    }, []);

    // Load messages when conversation changes
    useEffect(() => {
        setForwardingMessage(null);
        setDeletingMessage(null);
        setEditingMessage(null);
        setViewerState(null);
        historyRequest.current += 1;
        if (activeConversationId) {
            setMessages([]);
            setPage(1);
            setHasMore(true);
            loadMessages(1);
            // Also refresh call history for this conversation
            fetchCallHistory(activeConversationId);
        } else {
            setMessages([]);
            setCallHistory([]);
        }
    }, [activeConversationId, loadMessages]);

    useEffect(() => {
        setMessages(current => reconcileMessages(current));
        const unavailable = message => message && (!reconcileMessages([message])[0] || reconcileMessages([message])[0].deleted_at);
        setForwardingMessage(current => unavailable(current) ? null : current);
        setEditingMessage(current => unavailable(current) ? null : current);
        setViewerState(current => unavailable(current?.message) ? null : current);
    }, [deletionRevision, reconcileMessages]);

    // Listen to real-time message events for active conversation
    useEffect(() => {
        if (!activeConversationId) return;

        const echo = getEcho();
        if (!echo) {
            console.warn('Echo not initialized');
            return;
        }

        console.log('Joining conversation channel:', activeConversationId);
        const channel = echo.join(`talk.${activeConversationId}`);

        // Listen for new messages
        channel.listen('.talk.message.created', (e) => {
            console.log('New message received:', e);
            const newMessage = e.message;

            setMessages(prev => {
                const current = Array.isArray(prev) ? prev : [];

                // Check if message already exists (by real ID or temp ID being replaced)
                const newMessageSenderId = newMessage.sender_id || newMessage.sender?.id;
                const existingIndex = current.findIndex(m => {
                    const mSenderId = m.sender_id || m.sender?.id;
                    return m.id === newMessage.id ||
                        (typeof m.id === 'string' && m.id.startsWith('temp-') && mSenderId === newMessageSenderId);
                });

                if (existingIndex !== -1) {
                    // Replace existing optimistic or duplicate message
                    const updated = [...current];
                    updated[existingIndex] = { ...newMessage, deleted_at: current[existingIndex].deleted_at || newMessage.deleted_at };
                    return reconcileMessages(updated);
                }

                // Add new message (from other users) and scroll to bottom
                setTimeout(() => scrollToBottom(), 100);
                return mergeMessages(current, [newMessage]);
            });
        });

        // Listen for message updates
        channel.listen('.talk.message.updated', (e) => {
            console.log('Message updated:', e);
            setMessages(prev => {
                const current = Array.isArray(prev) ? prev : [];
                return mergeMessages(current, [e.message]);
            });
        });

        // Listen for message deletions
        channel.listen('.talk.message.deleted', (e) => {
            console.log('Message deleted:', e);
            applyDeletion(e);
        });

        const connection = echo.connector?.pusher?.connection;
        const refresh = () => {
            if (document.hidden) return;
            // Revalidate all loaded history, including older pages whose deletion
            // events may have been missed while offline or suspended.
            setMessages(current => current.filter(message => message.status === 'sending'));
            setPage(1);
            loadMessages(1);
        };
        channel.subscribed?.(refresh);
        connection?.bind('connected', refresh);
        document.addEventListener('visibilitychange', refresh);
        window.addEventListener('focus', refresh);

        // Cleanup
        return () => {
            console.log('Leaving conversation channel:', activeConversationId);
            echo.leave(`talk.${activeConversationId}`);
            connection?.unbind('connected', refresh);
            document.removeEventListener('visibilitychange', refresh);
            window.removeEventListener('focus', refresh);
        };
    }, [activeConversationId, currentUser, applyDeletion, reconcileMessages, mergeMessages, loadMessages, scrollToBottom]);

    // Fetch call history for the current conversation
    const fetchCallHistory = async (conversationId) => {
        if (!conversationId) return;
        setCallHistoryLoading(true);
        try {
            const res = await videoCallService.getCallHistory(conversationId);
            if (res.status) {
                setCallHistory(res.data?.meetings ?? []);
            } else {
                setCallHistory([]);
            }
        } catch {
            setCallHistory([]);
        } finally {
            setCallHistoryLoading(false);
        }
    };

    const handleScroll = (e) => {
        const container = e.target;
        if (container.scrollTop < 50 && hasMore && !isFetchingMore && !isLoading) {
            loadMessages(page + 1);
        }
    };

    const handleSendMessage = async ({ body, attachments }) => {
        // Validate input - need at least body or attachments
        const hasBody = body && body.trim();
        const hasAttachments = attachments && attachments.length > 0;
        if (!hasBody && !hasAttachments) return;

        // Ensure user is loaded
        if (!currentUser) {
            notify('error', 'User session not found. Please refresh the page.');
            return;
        }

        // Generate tempId outside try/catch so it's accessible in both blocks
        const tempId = `temp-${Date.now()}`;

        try {
            // Optimistic append
            const tempMsg = {
                id: tempId,
                body: body,
                sender_id: currentUser?.id,
                sender: currentUser,
                kind: attachments.length > 0 ? 'attachment' : 'text',
                uploads: attachments,
                created_at: new Date().toISOString(),
                status: 'sending'
            };
            setMessages(prev => {
                const current = Array.isArray(prev) ? prev : [];
                return [...current, tempMsg];
            });
            setTimeout(() => scrollToBottom('auto'), 50);

            // Send message to backend with temp_upload_ids
            const messageData = {};
            if (body && body.trim()) {
                messageData.body = body;
            }
            if (attachments && attachments.length > 0) {
                // Extract temp upload IDs (files already uploaded)
                messageData.temp_upload_ids = attachments
                    .filter(att => att.tempId)
                    .map(att => att.tempId);
            }

            const res = await chatService.sendMessage(activeConversationId, messageData);

            // Replace optimistic with real
            const realMessage = res.data?.message || res.data;
            setMessages(prev => {
                const current = Array.isArray(prev) ? prev : [];
                return reconcileMessages(current.map(m => m.id === tempId ? realMessage : m));
            });
        } catch (error) {
            console.error('Failed to send message', error);

            // Remove failed message from optimistic UI
            setMessages(prev => {
                const current = Array.isArray(prev) ? prev : [];
                return current.filter(m => m.id !== tempId);
            });

            // Show error notification
            if (error.status === 422 && error.data) {
                // Validation errors
                const errorMsg = error.message || 'Validation error';
                const firstError = Object.values(error.data)[0];
                const displayMsg = Array.isArray(firstError) ? firstError[0] : errorMsg;
                notify('error', displayMsg);
            } else {
                notify('error', error.message || 'Failed to send message');
            }
        }
    };

    const handleTypingStart = () => {
        // TODO: Send typing event to backend
        console.log('User started typing');
    };

    const handleTypingStop = () => {
        // TODO: Send typing stopped event to backend
        console.log('User stopped typing');
    };

    const handleEditMessage = (message) => {
        setEditingMessage(message);
    };

    const handleDeleteMessage = (message) => {
        setDeletingMessage(message);
    };

    const handleSearchResultClick = (message) => {
        // Scroll to the message
        if (messageRefs.current[message.id]) {
            messageRefs.current[message.id].scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });
            // Highlight the message briefly
            messageRefs.current[message.id].classList.add('talk-message-highlight');
            setTimeout(() => {
                messageRefs.current[message.id]?.classList.remove('talk-message-highlight');
            }, 2000);
        }
    };

    // Group messages by date
    const groupMessagesByDate = (messages) => {
        // Safety check: ensure messages is an array
        if (!Array.isArray(messages)) {
            return [];
        }

        const groups = [];
        let currentDate = null;

        messages.forEach((message, index) => {
            const messageDate = new Date(message.created_at).toDateString();

            if (messageDate !== currentDate) {
                groups.push({
                    type: 'date',
                    date: message.created_at,
                    key: `date-${messageDate}`
                });
                currentDate = messageDate;
            }

            // Check if message should be grouped with previous
            const prevMessage = messages[index - 1];
            const currentSenderId = message.sender_id || message.sender?.id;
            const prevSenderId = prevMessage?.sender_id || prevMessage?.sender?.id;
            const isGrouped = prevMessage &&
                prevSenderId === currentSenderId &&
                new Date(message.created_at) - new Date(prevMessage.created_at) < 60000; // 1 minute

            groups.push({
                type: 'message',
                message,
                isGrouped,
                key: message.id
            });
        });

        return groups;
    };

    const messageGroups = groupMessagesByDate(messages);



    const startCall = async (callType) => {
        if (meetingLoading || !conversation) return;

        try {
            setMeetingLoading(true);
            let payload = {};

            if (conversation.type === 'team') {
                const teamName = conversation.context?.name || 'Team';
                payload = {
                    team_id: conversation.context?.id,
                    title: `${teamName} Team Meeting`,
                    meeting_mode: 'group',
                    meeting_type: 'instant',
                    call_type: callType,
                    settings: {
                        recording: false,
                        screen_share: callType === 'video',
                    },
                };
            } else {
                const otherUser = conversation.users?.find(u => u.id !== currentUser?.id);
                if (!otherUser) {
                    notify('error', 'Cannot find user to call');
                    return;
                }

                payload = {
                    invitees: [otherUser.id],
                    title: `Call with ${otherUser.name}`,
                    meeting_mode: 'one_to_one',
                    meeting_type: 'instant',
                    call_type: callType,
                    settings: {
                        recording: false,
                        screen_share: callType === 'video',
                    },
                };
            }

            const response = await videoCallService.createInstant({
                ...payload,
                conversation_id: activeConversationId,
            });

            if (response.status && response.data?.meeting) {
                const joinResponse = await videoCallService.join(response.data.meeting.id);

                if (joinResponse.status) {
                    joinMeeting({
                        ...joinResponse.data.meeting,
                        tokens: joinResponse.data.tokens,
                    });

                    notify('success', 'Call started successfully');
                }
            }
        } catch (error) {
            console.error('Failed to start call:', error);
            notify('error', error.message || 'Failed to start call');
        } finally {
            setMeetingLoading(false);
        }
    };

    const handleVideoCall = () => startCall('video');
    const handleVoiceCall = () => startCall('voice');

    // After a call ends we refresh the history so the button appears
    const handleCallHistoryRefresh = () => fetchCallHistory(activeConversationId);

    // Empty state when no conversation selected
    // mobile-hidden keeps the CSS :has() rule showing the sidebar on mobile
    if (!activeConversationId) {
        return (
            <div className="talk-chat-window mobile-hidden">
                <div className="talk-chat-empty-state">
                    <div className="talk-chat-empty-icon">💬</div>
                    <h3>Select a conversation</h3>
                    <p>Choose a chat from the sidebar to start messaging</p>
                </div>
            </div>
        );
    }

    return (
        <div className="talk-chat-window">
            <ChatHeader
                conversation={conversation}
                currentUser={currentUser}
                typingUsers={typingUsers}
                onBack={() => setActiveConversationId(null)}
                onSearch={() => setShowSearch(true)}
                onInfoClick={() => { if (conversation?.type === 'team' || conversation?.type === 'group') setShowParticipantsModal(true); }}
                onAddMember={() => setShowAddMembersModal(true)}
                onVideoCall={handleVideoCall}
                onVoiceCall={handleVoiceCall}
                onBack={() => { setHideSidebar(false); setActiveConversationId(null); }}
                onShowHistory={() => setShowCallHistory(true)}
                hasCallHistory={callHistory.length > 0}
            />

            {showSearch && (
                <SearchMessages
                    messages={messages}
                    onClose={() => setShowSearch(false)}
                    onResultClick={handleSearchResultClick}
                />
            )}

            <div
                className="talk-chat-messages"
                ref={messagesContainerRef}
                onScroll={handleScroll}
            >
                {isLoading ? (
                    <MessageListSkeleton />
                ) : messageGroups.length === 0 ? (
                    <div className="talk-chat-no-messages">
                        <div className="talk-chat-empty-icon">💬</div>
                        <h4>No messages yet</h4>
                        <p>Start the conversation by sending a message below</p>
                    </div>
                ) : (
                    <>
                        {messageGroups.map((group) => {
                            if (group.type === 'date') {
                                return <DateDivider key={group.key} date={group.date} />;
                            }

                            const { message, isGrouped } = group;
                            // Handle both sender_id (optimistic) and sender.id (from backend)
                            const messageSenderId = message.sender_id || message.sender?.id;
                            const isSent = messageSenderId === currentUser?.id;
                            const sender = conversation?.users?.find(u => u.id === messageSenderId) || message.sender;

                            return (
                                <div
                                    key={group.key}
                                    ref={el => messageRefs.current[message.id] = el}
                                >
                                    <MessageBubble
                                        message={message}
                                        isSent={isSent}
                                        isGrouped={isGrouped}
                                        sender={sender}
                                        showAvatar={conversation?.type === 'team'}
                                        onEdit={handleEditMessage}
                                        onDelete={handleDeleteMessage}
                                        onForward={setForwardingMessage}
                                        onAttachmentClick={handleAttachmentClick}
                                    />
                                </div>
                            );
                        })}

                        {typingUsers.length > 0 && (
                            <TypingIndicator
                                user={typingUsers[0]}
                                showAvatar={conversation?.type === 'team'}
                            />
                        )}

                        <div ref={messagesEndRef} />
                    </>
                )}
            </div>

            {editingMessage && <EditMessageModal
                key={editingMessage.id}
                message={editingMessage}
                onClose={() => setEditingMessage(null)}
                onSaved={message => {
                    setMessages(current => mergeMessages(current, [message]));
                    fetchConversations(1, true);
                }}
            />}

            {deletingMessage && <DeleteMessageModal
                key={deletingMessage.id}
                message={deletingMessage}
                onClose={() => setDeletingMessage(null)}
                onDeleted={event => { applyDeletion(event); fetchConversations(1, true); }}
            />}

            {forwardingMessage && <ForwardMessageModal
                key={forwardingMessage.id}
                message={forwardingMessage}
                sourceId={activeConversationId}
                currentUserId={currentUser?.id}
                conversations={conversations}
                hasMore={hasMoreConversations}
                loadingMore={loadingMoreConversations}
                onLoadMore={loadMoreConversations}
                onClose={() => setForwardingMessage(null)}
                onForwarded={() => { fetchConversations(); notify('success', 'Message forwarded'); }}
            />}

            <MessageInput
                onSend={handleSendMessage}
                onTypingStart={handleTypingStart}
                onTypingStop={handleTypingStop}
                disabled={isLoading}
                placeholder="Type a message"
            />

            <ParticipantsModal
                isOpen={showParticipantsModal}
                onClose={() => setShowParticipantsModal(false)}
                conversation={conversation}
            />

            <TalkCallHistoryModal
                isOpen={showCallHistory}
                onClose={() => setShowCallHistory(false)}
                calls={callHistory}
                isLoading={callHistoryLoading}
                conversation={conversation}
                currentUser={currentUser}
            />

            {showAddMembersModal && (
                <TalkAddMembersModal
                    conversation={conversation}
                    onClose={() => setShowAddMembersModal(false)}
                    onAdded={() => {
                        // The same conversation was upgraded in-place (WhatsApp style).
                        // Refresh the conversation list so the type/participants update
                        // is reflected immediately in the sidebar and header.
                        fetchConversations(1);
                        setShowAddMembersModal(false);
                    }}
                />
            )}

            {viewerState?.kind === 'office' && (
                <TalkAttachmentOnlyOfficeViewerModal
                    conversationId={activeConversationId}
                    messageId={viewerState.message.id}
                    upload={viewerState.upload}
                    onClose={closeViewer}
                />
            )}

            {viewerState?.kind === 'pdf' && (
                <TalkAttachmentPdfViewerModal
                    upload={viewerState.upload}
                    onClose={closeViewer}
                />
            )}

            {viewerState?.kind === 'image' && (
                <TalkAttachmentImageViewerModal
                    upload={viewerState.upload}
                    messageUploads={viewerState.message.uploads}
                    onClose={closeViewer}
                />
            )}

            {viewerState?.kind === 'unsupported' && (
                <TalkAttachmentUnsupportedModal
                    upload={viewerState.upload}
                    onClose={closeViewer}
                />
            )}
        </div>
    );
};

export default ChatWindow;
